import Drawer from '@material-ui/core/Drawer';
import {Link} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {ApolloQueryResult} from 'apollo-client';
import * as _ from 'lodash';
import React, {useContext, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {envIsPublicCloud} from '../../config';
import {
  EntityIntegrationsQuery,
  EntityQuery,
  Secret,
  useSecretsQuery,
} from '../../generated/graphql';
import {createDismissibleBanner} from '../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../../state/viewer/hooks';
import DOC_URLS from '../../util/doc_urls';
import {isInvalidString} from '../../util/string';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../Banner';
import {ResizableDrawer} from '../common/ResizableDrawer/ResizableDrawer';
import * as S from '../Launch/Triggers/CreateTriggerDrawer/CreateTriggerDrawerLoaded.styles';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {useCreateGenericWebhook, useUpdateGenericWebhook} from './hooks';
import {TestWebhookDrawerInner} from './TestWebhookDrawerInner';
import {isIPv4Address, isLocalhostAddress, isValidURLFormat} from './urlUtil';
import {GenericWebhook} from './WebhooksTable';

const WEBHOOK_NAME_REGEX = new RegExp(/^[-\w\s]*$/);
const MAX_CHARS = 64;

type SecretsOptionType = {
  value?: string;
  label: string;
};

export type DrawerMode = 'Create' | 'Edit';

enum Steps {
  CONFIGURE_WEBHOOK,
  TEST_WEBHOOK,
}

enum InvalidURLType {
  URL_FORMAT,
  IS_IPV4,
  IS_LOCALHOST,
}

function getErrorMsg(invalidWebhookURLType: InvalidURLType | undefined) {
  if (invalidWebhookURLType == null) {
    return '';
  }
  switch (invalidWebhookURLType) {
    case InvalidURLType.IS_IPV4:
      return 'Invalid format: IP-based URLs are not supported.';
    case InvalidURLType.IS_LOCALHOST:
      return 'Invalid: localhost is not allowed.';
    case InvalidURLType.URL_FORMAT:
      return 'Invalid format. Please enter a valid URL.';
    default:
      console.warn(
        `unhandled invalid webhook url type: ${invalidWebhookURLType}`
      );
      return '';
  }
}

type WebhookDrawerProps = {
  open: boolean;
  closeDrawer: () => void;
  drawerMode: DrawerMode;
  entityName: string;
  isPersonalEntity?: boolean;
  refetch?: () =>
    | Promise<ApolloQueryResult<EntityQuery>>
    | Promise<ApolloQueryResult<EntityIntegrationsQuery>>;
  existingWebhooks?: GenericWebhook[];
  webhook?: GenericWebhook;
};

export const WebhookDrawer = ({
  open,
  closeDrawer,
  drawerMode,
  entityName,
  refetch,
  existingWebhooks,
  isPersonalEntity,
  webhook,
}: WebhookDrawerProps) => {
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  const emptyWebhook: GenericWebhook = {
    id: '',
    name: '',
    urlEndpoint: '',
    createdAt: new Date(0).toISOString(),
  };
  const [step, setStep] = useState(Steps.CONFIGURE_WEBHOOK);
  const isEditMode = drawerMode === 'Edit';
  const [isEditButtonDisabled, setIsEditButtonDisabled] = useState(false);
  const [isCreateButtonDisabled, setIsCreateButtonDisabled] = useState(false);
  const [newWebhook, setNewWebhook] = useState(emptyWebhook);
  const [webhookNameExists, setWebhookNameExists] = useState(false);
  const [isInvalidWebhookName, setIsInvalidWebhookName] = useState(false);
  const [isInvalidWebhookURL, setIsInvalidWebhookURL] = useState(false);
  const [invalidWebhookURLType, setInvalidWebhookURLType] = useState<
    InvalidURLType | undefined
  >();
  const [isMaxCharsWebhookName, setIsMaxCharsWebhookName] = useState(false);

  const {updateGenericWebhook, isLoading: isEditLoading} =
    useUpdateGenericWebhook();
  const {createGenericWebhook, isLoading: isCreateLoading} =
    useCreateGenericWebhook();

  const resetParams = () => {
    if (drawerMode === 'Create') {
      setNewWebhook(emptyWebhook);
    } else if (webhook != null) {
      setNewWebhook(webhook);
    }
    setStep(Steps.CONFIGURE_WEBHOOK);
    resetInvalidWebhookURL();
    setIsInvalidWebhookName(false);
    setIsMaxCharsWebhookName(false);
  };

  const onClose = () => {
    resetParams();
    closeDrawer();
  };

  const resetInvalidWebhookURL = () => {
    setInvalidWebhookURLType(undefined);
    setIsInvalidWebhookURL(false);
  };

  const viewer = useViewer();
  const username = viewer?.username;
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  useEffect(() => {
    if (isEditMode && webhook != null) {
      setNewWebhook(webhook);
    }
  }, [isEditMode, webhook]);

  useEffect(() => {
    const existingWebhookName = existingWebhooks?.some(
      w => w?.name === newWebhook.name && w?.name !== webhook?.name
    );
    setWebhookNameExists(existingWebhookName ?? false);

    setIsCreateButtonDisabled(
      (isCreateLoading ||
        newWebhook.name === '' ||
        newWebhook.urlEndpoint === '' ||
        existingWebhookName ||
        isInvalidWebhookName ||
        isInvalidWebhookURL) ??
        false
    );

    setIsEditButtonDisabled(
      (_.isEqual(webhook, newWebhook) ||
        isEditLoading ||
        newWebhook.name === '' ||
        newWebhook.urlEndpoint === '' ||
        existingWebhookName ||
        isInvalidWebhookName ||
        isInvalidWebhookURL) ??
        false
    );
  }, [
    isCreateLoading,
    isEditLoading,
    webhook,
    newWebhook,
    newWebhook.name,
    newWebhook.urlEndpoint,
    existingWebhooks,
    isInvalidWebhookURL,
    isInvalidWebhookName,
    webhook?.name,
    setWebhookNameExists,
    setIsCreateButtonDisabled,
  ]);

  const {
    data: entitySecrets,
    loading: entitySecretsLoading,
    error: entitySecretsError,
  } = useSecretsQuery({
    variables: {
      entityName,
    },
    skip: entityName === '' || isPersonalEntity || !open,
  });

  const entitySecretsOptions: SecretsOptionType[] =
    entitySecrets?.entity?.secrets?.map((secret: Secret) => ({
      value: secret.name,
      label: secret.name,
    })) ?? [];

  if (entitySecretsOptions.length > 0) {
    // Add option that allows user to clear selection
    entitySecretsOptions.unshift({
      value: undefined,
      label: 'None',
    });
  }

  if (entitySecretsError) {
    console.warn(entitySecretsError);
    closeDrawer();
    toast.error('Error loading secrets');
  }
  if (entitySecretsLoading) {
    return <Loader active name="create-webhooks-load-entity-secrets" />;
  }
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="h-full w-full px-24 py-20">
            <div className="flex h-full w-full flex-col">
              <div className="mb-20 flex items-center">
                <div className="text-xl font-semibold leading-8">
                  {isEditMode && webhook != null
                    ? `Edit webhook ${webhook.name}`
                    : 'Create a new webhook'}
                </div>
                <div className="flex-1 text-right">
                  <Button
                    onClick={onClose}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>
              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="w-full flex-1 overflow-auto p-4">
                {step === Steps.CONFIGURE_WEBHOOK && (
                  <>
                    <div className="mb-16 mt-20">
                      <Banner variant="info">
                        <BannerFlexWrapper>
                          <Icon name="lightbulb-info" />
                          <BannerTextWrapper>
                            <span>
                              Webhooks created will be available within the{' '}
                              {isPersonalEntity ? "user's" : "team's"} (
                              {entityName}) scope.
                              <br />
                              <br />
                              We will construct the POST request we send based
                              on your inputs for url, access token, and secret.
                              Visit{' '}
                              <Link
                                style={{textDecoration: 'underline'}}
                                to={`${DOC_URLS.artifactAutomations}#troubleshoot-your-webhook`}>
                                documentation
                              </Link>{' '}
                              ("Bash script" tab) to preview.
                            </span>
                          </BannerTextWrapper>
                        </BannerFlexWrapper>
                      </Banner>
                    </div>
                    <div className="my-16 flex w-full items-center text-base font-semibold text-moon-500">
                      Step {step + 1} of 2 - Enter webhook details
                    </div>
                    <div className="mb-4 flex w-full items-center">
                      <div className="text-base font-semibold leading-8">
                        Name*
                      </div>
                    </div>
                    <div className="text-base font-normal">
                      <TextField
                        placeholder="Enter webhook name Ex. MS teams"
                        value={newWebhook.name ?? ''}
                        onChange={value => {
                          setIsInvalidWebhookName(
                            isInvalidString(value, WEBHOOK_NAME_REGEX)
                          );
                          setIsMaxCharsWebhookName(value.length > MAX_CHARS);
                          setNewWebhook({...newWebhook, name: value});
                        }}
                        autoFocus={true}
                        errorState={webhookNameExists || isInvalidWebhookName}
                      />
                    </div>
                    {isInvalidWebhookName && (
                      <div className="mt-8 leading-6 text-red-500">
                        Webhook name can only contain the following special
                        characters: -, _.
                      </div>
                    )}
                    {isMaxCharsWebhookName && (
                      <div className="mt-8 leading-6 text-red-500">
                        Webhook name can only contain {MAX_CHARS} characters.
                      </div>
                    )}
                    {webhookNameExists && (
                      <div className="mt-8 leading-6 text-red-500">
                        Webhook name already exists
                      </div>
                    )}
                    <div className="mb-4 mt-16 flex w-full items-center">
                      <div className="text-base font-semibold leading-8">
                        URL*
                      </div>
                    </div>
                    <div className="text-base font-normal">
                      <TextField
                        placeholder="Enter endpoint URL for the webhook"
                        value={newWebhook.urlEndpoint ?? ''}
                        onChange={value => {
                          resetInvalidWebhookURL();
                          setNewWebhook({...newWebhook, urlEndpoint: value});
                        }}
                        onBlur={value => {
                          if (isIPv4Address(value)) {
                            setIsInvalidWebhookURL(true);
                            setInvalidWebhookURLType(InvalidURLType.IS_IPV4);
                          } else if (isLocalhostAddress(value)) {
                            setIsInvalidWebhookURL(true);
                            setInvalidWebhookURLType(
                              InvalidURLType.IS_LOCALHOST
                            );
                          } else if (!isValidURLFormat(value)) {
                            setIsInvalidWebhookURL(true);
                            setInvalidWebhookURLType(InvalidURLType.URL_FORMAT);
                          } else {
                            resetInvalidWebhookURL();
                          }
                        }}
                        errorState={isInvalidWebhookURL}
                      />
                    </div>
                    {isInvalidWebhookURL && newWebhook.urlEndpoint !== '' && (
                      <div className="mt-8 leading-6 text-red-500">
                        {getErrorMsg(invalidWebhookURLType)}
                      </div>
                    )}
                    {!isPersonalEntity && (
                      <>
                        <div className="mb-4 mt-16 flex w-full items-center">
                          <div className="text-base font-semibold leading-8">
                            Access token
                          </div>
                        </div>
                        <Select<SecretsOptionType>
                          options={entitySecretsOptions}
                          value={
                            newWebhook.accessTokenRef
                              ? {
                                  value: newWebhook.accessTokenRef,
                                  label: newWebhook.accessTokenRef,
                                }
                              : null
                          }
                          placeholder="Select an access token (optional)"
                          onChange={(d: SecretsOptionType | null) => {
                            setNewWebhook({
                              ...newWebhook,
                              accessTokenRef: d != null ? d.value : undefined,
                            });
                          }}
                        />
                        <S.SelectOptionInfoText>
                          Secure your webhook requests by using access tokens to
                          authorize the sender. Access tokens are created in
                          team settings under "Secrets."
                        </S.SelectOptionInfoText>
                        <AuthHeaderInfoBanner
                          orgName={orgName}
                          userID={username ?? ''}
                        />

                        <div className="mb-4 mt-16 flex w-full items-center">
                          <div className="text-base font-semibold leading-8">
                            Secret
                          </div>
                        </div>
                        <Select<SecretsOptionType>
                          options={entitySecretsOptions}
                          value={
                            newWebhook.secretRef
                              ? {
                                  value: newWebhook.secretRef,
                                  label: newWebhook.secretRef,
                                }
                              : null
                          }
                          placeholder="Select a secret (optional)"
                          onChange={(d: SecretsOptionType | null) => {
                            setNewWebhook({
                              ...newWebhook,
                              secretRef: d != null ? d.value : undefined,
                            });
                          }}
                        />
                        <S.SelectOptionInfoText>
                          Ensure the integrity of your webhook payload by using
                          secrets for authentication. Secrets are created in
                          team settings under "Secrets."
                        </S.SelectOptionInfoText>
                      </>
                    )}
                  </>
                )}
                {step === Steps.TEST_WEBHOOK && (
                  <>
                    <div className="my-16 flex w-full items-center text-base font-semibold text-moon-450">
                      Step {step + 1} of 2 - Test the webhook (optional)
                    </div>
                    <TestWebhookDrawerInner
                      entityName={entityName}
                      webhook={newWebhook}
                    />
                  </>
                )}
              </div>
              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="flex justify-between pt-20">
                {step === Steps.CONFIGURE_WEBHOOK ? (
                  <div className="min-w-50 inline-flex content-start align-middle">
                    <div className="text-base leading-8 text-moon-600">
                      You may test webhook on the next step.
                    </div>
                  </div>
                ) : (
                  <div className="min-w-50 inline-flex content-start align-middle">
                    <Button
                      variant="secondary"
                      size="large"
                      icon="back"
                      onClick={() => {
                        setStep(step - 1);
                      }}>
                      Back
                    </Button>
                  </div>
                )}
                <div className="min-w-50 inline-flex gap-8">
                  {isEditMode ? (
                    <Button
                      variant="primary"
                      disabled={isEditButtonDisabled}
                      onClick={async () => {
                        await updateGenericWebhook(
                          newWebhook.id,
                          newWebhook.urlEndpoint.trim(),
                          newWebhook.name.trim(),
                          newWebhook.accessTokenRef,
                          newWebhook.secretRef
                        );
                        await refetch?.();
                        closeDrawer();
                      }}>
                      Update webhook
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      disabled={isCreateButtonDisabled}
                      onClick={async () => {
                        await createGenericWebhook(
                          entityName,
                          newWebhook.urlEndpoint.trim(),
                          newWebhook.name.trim(),
                          newWebhook.accessTokenRef,
                          newWebhook.secretRef
                        );
                        await refetch?.();
                        onClose();
                      }}>
                      Create webhook
                    </Button>
                  )}
                  {step !== Steps.TEST_WEBHOOK && (
                    <Button
                      variant="secondary"
                      size="large"
                      endIcon="forward-next"
                      onClick={() => {
                        setStep(step + 1);
                      }}
                      disabled={isCreateButtonDisabled}>
                      Test
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};

type AuthHeaderInfoBannerProps = {
  userID: string;
  orgName: string;
};
const AuthHeaderInfoBanner = createDismissibleBanner<AuthHeaderInfoBannerProps>(
  {
    id: ({userID, orgName}) => `webhook-auth-header-info:${orgName}:${userID}`,
    Component: ({onDismiss}) => {
      return (
        <Tailwind>
          <S.BannerWithMargin variant="warning">
            <BannerFlexWrapper>
              <Icon name="info" />
              <BannerTextWrapper>
                <span>
                  The authorization header will take the $ACCESS_TOKEN as input
                  to the bearer token: -H "Authorization: Bearer $ACCESS_TOKEN"
                </span>
              </BannerTextWrapper>
              <button
                className="flex items-center border-none bg-transparent font-semibold text-sienna-600 hover:cursor-pointer hover:text-sienna-500"
                onClick={onDismiss}>
                <Icon name={'close'} />
              </button>
            </BannerFlexWrapper>
          </S.BannerWithMargin>
        </Tailwind>
      );
    },
  }
);
